import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/currencyConversion";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { useLanguage } from "@/contexts/LanguageContext";
import { DashboardCard } from "@/components/ui/dashboard-card";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface RecentExpense {
  id: string;
  amount: number;
  merchant: string;
  category: string;
  expense_date: string;
  currency: string;
}

interface RecentTransactionsProps {
  userId: string;
  refreshTrigger: number;
}

const CATEGORY_DOT: Record<string, string> = {
  Housing: "bg-purple-500",
  Food: "bg-green-500",
  Transport: "bg-blue-500",
  Fun: "bg-pink-500",
  Shopping: "bg-orange-500",
  Health: "bg-red-500",
  Coffee: "bg-amber-600",
  Utilities: "bg-yellow-500",
  "Bills/Other": "bg-gray-500",
};

export const RecentTransactions = ({ userId, refreshTrigger }: RecentTransactionsProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<RecentExpense[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const { data } = await supabase
        .from("expenses")
        .select("id, amount, merchant, category, expense_date, currency")
        .eq("user_id", userId)
        .order("expense_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(7);

      setExpenses(data || []);
      setIsLoading(false);
    };
    load();
  }, [userId, refreshTrigger]);

  if (isLoading) {
    return (
      <DashboardCard noPadding className="overflow-hidden">
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="w-8 h-8 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </DashboardCard>
    );
  }

  if (expenses.length === 0) return null;

  // Group by date label
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const grouped: Record<string, RecentExpense[]> = {};
  for (const exp of expenses) {
    const d = new Date(exp.expense_date + "T00:00:00");
    let label: string;
    if (d.toDateString() === today.toDateString()) {
      label = t("history.today");
    } else if (d.toDateString() === yesterday.toDateString()) {
      label = t("history.yesterday");
    } else {
      label = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    }
    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(exp);
  }

  return (
    <DashboardCard noPadding className="overflow-hidden">
      <div className="p-4 pb-0">
        <h3 className="text-sm font-semibold text-muted-foreground mb-3">
          Recent
        </h3>

        <div className="space-y-4">
          {Object.entries(grouped).map(([dateLabel, items]) => (
            <div key={dateLabel}>
              <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wide mb-2">
                {dateLabel}
              </p>
              <div className="space-y-1">
                {items.map((exp) => (
                  <div
                    key={exp.id}
                    className="flex items-center justify-between py-2 px-1"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-lg leading-none">
                        {getCategoryIcon(exp.category)}
                      </span>
                      <span className="text-sm font-medium truncate">
                        {exp.merchant}
                      </span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums shrink-0 ml-2">
                      {formatCurrency(exp.amount, exp.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* View all link */}
      <button
        type="button"
        onClick={() => navigate("/expenses")}
        className="w-full py-3 px-4 flex items-center justify-center gap-1 text-sm font-medium text-primary hover:bg-accent/50 transition-colors border-t mt-3"
      >
        View all expenses
        <ChevronRight className="h-4 w-4" />
      </button>
    </DashboardCard>
  );
};
